import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CreateOrganisationDialog } from "@/components/CreateOrganisationDialog";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDateTime } from "@/lib/format";
import { PlusIcon } from "lucide-react";

type Organisation = components["schemas"]["OrganisationResponse"];
type OrganisationSearchHit = components["schemas"]["OrganisationAdminSearchHit"];

// Trigram search returns top matches rather than a page, so searching hides the pager.
const SEARCH_LIMIT = 20;

interface ClientOrganisationsTableProps {
  clientId: string;
}

export function ClientOrganisationsTable({ clientId }: ClientOrganisationsTableProps) {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);
  const searchTerm = debouncedSearch.trim();
  const searching = searchTerm.length > 0;

  const pagination = useCursorPagination({ initialLimit: 20 });

  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [hits, setHits] = useState<OrganisationSearchHit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  const prevSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      pagination.reset();
    }
  }, [debouncedSearch, pagination.reset]);

  // Fuzzy search (id, name, email, tax and registration numbers), unlike the paged list's exact-name filter.
  useEffect(() => {
    if (!searching) return;
    let cancelled = false;

    async function searchOrganisations() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/organisations/search", {
        params: { query: { q: searchTerm, clientId, limit: SEARCH_LIMIT } },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to search organisations.");
      } else {
        setHits(data ?? []);
      }
      setLoading(false);
    }

    searchOrganisations();
    return () => {
      cancelled = true;
    };
  }, [clientId, searching, searchTerm, refetchKey]);

  useEffect(() => {
    if (searching) return;
    let cancelled = false;

    async function fetchOrganisations() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/organisations", {
        params: {
          query: {
            ClientId: clientId,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load organisations.");
      } else {
        setOrganisations(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchOrganisations();
    return () => {
      cancelled = true;
    };
  }, [clientId, searching, pagination.cursor, pagination.limit, refetchKey]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  // One row shape for both modes: the search hit carries no createdAt, so the trailing column
  // shows the contact email instead.
  const rows = searching
    ? hits.map((h) => ({ id: h.id, name: h.name, trailing: h.contactEmail ?? "-" }))
    : organisations.map((o) => ({ id: o.id, name: o.name, trailing: formatDateTime(o.createdAt) }));

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <Input
          placeholder="Search by name, ID, email, tax no..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-sm"
        />
        <div className="flex items-center gap-3">
          {loading && <Spinner className="size-5" />}
          <Button onClick={() => setCreateDialogOpen(true)}>
            <PlusIcon className="mr-2 size-4" />
            Create Organisation
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>{searching ? "Contact email" : "Created"}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-muted-foreground h-24 text-center">
                  {searching
                    ? `No organisations matching "${searchTerm}".`
                    : "No organisations found."}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">
                    <Link to={`/organisations/${row.id}`} className="hover:underline">
                      {row.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/organisations/${row.id}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-secondary/80">
                        {row.id}
                      </Badge>
                    </Link>
                  </TableCell>
                  <TableCell>{row.trailing}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {searching ? (
        <p className="text-muted-foreground mt-3 text-xs">
          Showing the closest matches across every organisation of this client
          {hits.length === SEARCH_LIMIT ? ` (top ${SEARCH_LIMIT})` : ""}.
        </p>
      ) : (
      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
      )}

      <CreateOrganisationDialog
        clientId={clientId}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={() => setRefetchKey((k) => k + 1)}
      />
    </div>
  );
}
